import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageWrapper from '../../../components/pageComponents/PageWrapper';
import SearchContainer from '../../../components/pageComponents/SearchContainer';
import ModalEnabled from '../../../components/modalComponents/ModalEnabled';
import ConfirmarPostulacionModal from '../modales/ConfirmarPostulacionModal';
import PaginatorV2 from '../../../components/pageComponents/PaginatorV2';
import { useManagePostRequest } from '../../../hooks/useManagePostRequest/useManagePostRequest';
import useDidMountEffect from '../../../hooks/useDidMountEffect/useDidMountEffect';
import { listarPublicadasAPI } from '../../../consumers/backendApisUrls';
import PostularConvocatoriaModal from '../modales/PostularConvocatoriaModal';
import FirmaColaborador from '../modales/FirmaColaborador';
import { ESTAPAS_POSTULACION_TP, ESTADOS_FIRMA_TP } from '../../../constants/hardCodedConstants';
import {
    TIPO_LABEL, TIPO_ICONO, formatearFecha,
    ContentWrapper, SectionTitle, SectionDescription, CardsList, ConvocatoriaCard,
    IconContainer, CardInformation, CardTitle, CardDetails, ProgressDetails,
    TypeBadge, ClosingDate, ApplyButton, ClosedBadge, RegisteredBadge, EmptyState,
} from './shared';

// ──────────────────────────────────────────────────────────────────────────
// Pestaña "Convocatorias disponibles" de "Mis Traslados y Promociones" (Pieza E).
// Antes del 30/09/2026 esto era toda la página; se separó a su propio componente
// para vivir dentro de una pestaña de TabsHeader (ver MisTrasladosYPromociones.jsx),
// así la búsqueda por nombre solo aplica aquí y no a "Entrevistas por completar".
//
// Lista las convocatorias a cuya población objetivo pertenece el colaborador
// (HU-TR-03 / HU-PR-03 CA-01), sin importar el estado de la campaña: el backend
// solo excluye En edición (66) y Cancelado (25). Si ya se puede postular o no lo
// dicen las columnas EstaDisponible y YaPostulo de cada fila. "Postularme" abre el modal de postulación, que
// persiste TP_Postulante + TP_PostulanteDeclaracion + TP_Feedback(+Detalle).
//
// La búsqueda y la paginación las resuelve el backend (POST ListarPublicadas
// con NombreConvocatoria/Page/PageSize); no se filtra en cliente.
//
// El badge "Inscrito" se resuelve con PuedePostular por cada convocatoria de
// la página (10 como máximo, en paralelo), porque ListarPublicadas no devuelve
// si el empleado ya postuló. Si se agrega ese dato al response de Java, esta
// consulta extra se puede eliminar.
// ──────────────────────────────────────────────────────────────────────────

const ConvocatoriasDisponibles = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [searchCriteria, setSearchCriteria] = useState('');
    const [modalConfirmacion, setModalConfirmacion] = useState({ isEnable: false });
    const [paginator, setPaginator] = useState({ CurrentPage: 1 });
    const [convocatorias, setConvocatorias] = useState([]);
    const [modalEnabled, setModalEnabled] = useState({ isEnable: false });

    const [managePostRequest] = useManagePostRequest();

    const successManageSearchCallback = ({ data }) => {
        const { Items, TotalItems, FirsItemPage, LastItemPage, TotalPages, CurrentPage } = data;
        setConvocatorias(Items ?? []);
        setPaginator({ TotalItems, FirsItemPage, LastItemPage, TotalPages, CurrentPage });
    };

    const manageSearch = async () => {
        await managePostRequest(listarPublicadasAPI(), {
            NombreConvocatoria: searchCriteria,
            Page: paginator.CurrentPage,
            PageSize: 10,
        }, successManageSearchCallback, false);
    };

    const init = async () => {
        setIsLoading(true);
        try {
            await manageSearch();
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useDidMountEffect(() => {
        setIsLoading(true);
        manageSearch().finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginator.CurrentPage]);

    const cleanSearch = () => setSearchCriteria('');

    const handlePostular = (convocatoria) => {
        setModalEnabled({ isEnable: true, component: PostularConvocatoriaModal, data: { row: convocatoria } });
    };

    // HU-TR-07: FirmaColaborador espera postulante.idPostulante (TP_Postulante.IdPostulante),
    // que ListarPublicadas ya trae porque la fila viene del INNER JOIN contra el propio
    // postulante logueado (ver CONVOCATORIA_PUBLICADAS_JOIN en el lambda de lectura).
    const handleFirmar = (convocatoria) => {
        setModalEnabled({ isEnable: true, component: FirmaColaborador, data: { postulante: convocatoria } });
    };

    const openPostulationConfirmation = (convocatoria) => {
        setModalConfirmacion({
            isEnable: true,
            component: ConfirmarPostulacionModal,
            data: { convocatoria, onContinue: handlePostular },
        });
    };

    return (
        <PageWrapper isLoading={isLoading}>
            <ModalEnabled modalEnabled={modalEnabled} setModalEnabled={setModalEnabled} refreshPage={manageSearch} />
            <ModalEnabled
                modalEnabled={modalConfirmacion}
                setModalEnabled={setModalConfirmacion}
            />
            <SearchContainer
                searchCriteria={searchCriteria}
                setSearchCriteria={setSearchCriteria}
                setPaginator={setPaginator}
                manageSearch={manageSearch}
                addCleanSearch={cleanSearch}
            />

            <ContentWrapper>
                <SectionTitle>Convocatorias disponibles</SectionTitle>
                <SectionDescription>Revisa las convocatorias activas en las que puedes participar</SectionDescription>

                <CardsList>
                    {convocatorias.map(convocatoria => {
                        const tipo = TIPO_LABEL[convocatoria.tipoProceso] ?? convocatoria.tipoProceso;
                        // Lo resuelve el backend (columna YaPostulo de LIST_CONVOCATORIAS_PUBLICADAS):
                        // la fila que siembra la publicacion queda en estado 3 (invitado) y no
                        // cuenta como postulacion. Antes se preguntaba con un PuedePostular por
                        // cada tarjeta de la pagina.
                        const inscrito = convocatoria.yaPostulo === true;
                        const estaDisponible = convocatoria.estaDisponible === true;
                        // HU-TR-07 CA-02: idEtapaPostulacion es el id numerico de
                        // TP_Postulante.IdEtapaPostulante (via ListarPublicadas); no
                        // depende del texto de DesEtapaPostulacion.
                        const enEtapaFirma = convocatoria.idEtapaPostulacion === ESTAPAS_POSTULACION_TP.FIRMA_CIERRE;

                        const etapa = convocatoria.desEtapaPostulacion ?? convocatoria.desEtapa ?? '-';
                        const estadoPostulacion = convocatoria.desEstadoPostulacion ?? convocatoria.estadoPostulacion ?? '-';

                        const proximamente = convocatoria.proximamente === true;
                        return (
                            <ConvocatoriaCard key={convocatoria.idConvocatoria}>
                                <IconContainer tipo={tipo}>
                                    <FontAwesomeIcon icon={TIPO_ICONO[convocatoria.tipoProceso] ?? 'exchange-alt'} />
                                </IconContainer>

                                <CardInformation>
                                    <CardTitle>{convocatoria.nombreConvocatoria}</CardTitle>
                                    <CardDetails>
                                        <TypeBadge tipo={tipo}>{tipo}</TypeBadge>
                                        <ClosingDate>Cierre: {formatearFecha(convocatoria.fechaHoraFin)}</ClosingDate>
                                    </CardDetails>
                                    <ProgressDetails>
                                        <span>Etapa: <strong>{etapa}</strong></span>
                                        <span>Estado: <strong>{estadoPostulacion}</strong></span>
                                    </ProgressDetails>
                                </CardInformation>

                                {/* CA-03: "Postularme" solo si todavía no se inscribió Y la
                                    convocatoria admite postulaciones. estaDisponible lo
                                    resuelve el backend (vigencia + estados 19/25); sin este
                                    chequeo una campaña vencida ofrecería el botón activo y
                                    la postulación fallaría recién al enviarla. */}
                                {/* HU-TR-07 CA-02: si llegó a la etapa de firma, lo que
                                    corresponde es firmar, no "Inscrito". Va primero
                                    porque para entonces yaPostulo es true y si no, la
                                    rama de arriba se lo comería. */}
                                {enEtapaFirma ? (
                                    <ApplyButton type="button" onClick={() => handleFirmar(convocatoria)}>
                                        <FontAwesomeIcon icon="file-signature" className="mr-1" />
                                        {convocatoria.idEstadoPostulacion === ESTADOS_FIRMA_TP.OBSERVADA
                                            ? 'Subsanar'
                                            : 'Firmar'}
                                    </ApplyButton>
                                ) : inscrito ? (
                                    <RegisteredBadge>
                                        <FontAwesomeIcon icon="check" /> Inscrito
                                    </RegisteredBadge>
                                ) : !estaDisponible ? (
                                    proximamente ? (
                                        <ClosedBadge title="Esta convocatoria todavía no está abierta para postular">
                                            Próximamente
                                        </ClosedBadge>
                                    ) : (
                                        <ClosedBadge title="El plazo de esta convocatoria ya cerró">
                                            Cerrada
                                        </ClosedBadge>
                                    )
                                ) : (
                                    <ApplyButton type="button" onClick={() => handlePostular(convocatoria)}>
                                        Postularme
                                    </ApplyButton>
                                )}
                            </ConvocatoriaCard>
                        );
                    })}
                </CardsList>

                {!isLoading && convocatorias.length === 0 && (
                    <EmptyState>No se encontraron convocatorias para la búsqueda realizada.</EmptyState>
                )}

                {paginator.TotalPages > 1 &&
                    <PaginatorV2 paginator={paginator} setPaginator={setPaginator} manageSearch={manageSearch} />
                }
            </ContentWrapper>
        </PageWrapper>
    );
};

export default ConvocatoriasDisponibles;
